// @ts-ignore — highcharts 6.2.0 has no bundled types
import Highcharts from 'highcharts';
// @ts-ignore
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, EmptyState, IconTooltip, Tag, useFeatureUser, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  isWithinPostCampaignWindow,
  POST_CAMPAIGN_DAYS
} from '@/components/driveToStore/common/helpers/campaignDates.helper';
import PeriodNavigator from '@/components/driveToStore/common/PeriodNavigator/PeriodNavigator';
import { ViewToggle } from '@/components/ViewToggle/ViewToggle';
import { type FootfallTrafficStatsResponse, getFootfallTrafficStats } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { filterTrafficDataByRange, type Granularity, getPeriodInfo, getPeriodRange } from './granularity.helpers';
import { buildSeries, getDayStart, getTodayTimestamp } from './helpers';

import './trafficAnalysis.scss';

import FootfallReportSkeleton from '@/components/driveToStore/FootfallReport/FootfallReportSkeleton';

export type ChartMode = 'areas' | 'columns';

export type TrafficGroupBy = 'hour' | 'day';

export type SeriesFilter = 'all' | 'baseline' | 'uplift';

const AREAS_CHART_PERMISSION = 'reportingTrafficAnalysisAreas';
const TRAFFIC_ANALYSIS_FILTER_FLAG = 'd2s_traffic_analysis_filter';

const BASELINE_COLOR = '#8C9DFF';
const UPLIFT_COLOR = '#21CDCD';
const TODAY_LINE_COLOR = 'rgba(123, 159, 225, 0.5)';
const TODAY_LABEL_COLOR = 'rgba(140, 157, 255, 0.5)';
const DAY_TICK_INTERVAL_MS = 24 * 60 * 60 * 1000;
const HOURLY_TICK_INTERVAL_MS = 3 * 60 * 60 * 1000;
const MAX_X_AXIS_LABELS = 20;
const CHART_HEIGHT_PX = 400;

const GRANULARITY_OPTIONS: Granularity[] = ['all', 'month', 'week', 'day'];
const FILTER_OPTIONS: SeriesFilter[] = ['all', 'baseline', 'uplift'];

type Props = {
  campaignEnd: string;
  campaignStart: string;
  entityType: string;
  entityId: string;
  timezone: string;
  userId: string;
  venueIds?: string[];
};

type State = {
  chartMode: ChartMode;
  granularity: Granularity;
  periodIndex: number;
  showPredictions: boolean;
  seriesFilter: SeriesFilter;
};

export default function TrafficAnalysis({
  campaignEnd,
  campaignStart,
  entityType,
  entityId,
  timezone,
  userId,
  venueIds
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const hasAreasPermission = checkUserResourcePermissions(AREAS_CHART_PERMISSION, user.toJSON(), userRoles);
  const hasTrafficAnalysisFilter = useFeatureUser(TRAFFIC_ANALYSIS_FILTER_FLAG);

  const [{ chartMode, granularity, periodIndex, showPredictions, seriesFilter }, setState] = useState<State>({
    chartMode: 'areas',
    granularity: 'all',
    periodIndex: 0,
    seriesFilter: 'uplift',
    showPredictions: false
  });

  const effectiveChartMode: ChartMode = hasAreasPermission ? chartMode : 'columns';
  const showPeriodNavigator = effectiveChartMode !== 'areas' || hasTrafficAnalysisFilter;
  const trafficGroupBy: TrafficGroupBy = effectiveChartMode === 'columns' ? 'hour' : 'day';
  const isHourly = trafficGroupBy === 'hour' && granularity === 'day';

  const canShowPredictions = isWithinPostCampaignWindow(campaignEnd, timezone);
  const effectiveShowPredictions = showPredictions && canShowPredictions;

  const trafficRequestToken: Record<string, any> = {};

  useEffect(() => {
    return () => {
      trafficRequestToken.cancel?.();
    };
  }, []);

  const { data, isLoading, status } = useQueryFetch<FootfallTrafficStatsResponse['trafficAnalysis'] | null>(
    ['footfall-trafficStats', entityType, entityId, trafficGroupBy, venueIds],
    async () => {
      const data = await getFootfallTrafficStats(
        entityType,
        entityId,
        userId,
        undefined,
        venueIds,
        trafficGroupBy,
        trafficRequestToken
      );
      return data.trafficAnalysis;
    }
  );

  if (status === 'error') {
    throw new Error('Failed to load traffic analysis data');
  }

  if (isLoading || !data) {
    return <FootfallReportSkeleton variant="chart" />;
  }

  const handleChange = (field: keyof State, value: State[typeof field]) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const allDates = [...data.baseline, ...data.baselinePrediction, ...data.uplift, ...data.upliftPrediction].map(p =>
    new Date(p.date).getTime()
  );
  const dataEndBoundMs = allDates.length ? Math.max(...allDates) : undefined;
  const periodInfo = getPeriodInfo(granularity, campaignStart, campaignEnd, timezone, dataEndBoundMs);
  const periodRange = getPeriodRange(granularity, periodIndex, campaignStart, timezone);
  const windowedData = filterTrafficDataByRange(data, periodRange);
  const isFuturePeriod =
    periodRange !== null && periodRange.start >= getTodayTimestamp(timezone) && !effectiveShowPredictions;

  const handleGranularityChange = (newGranularity: Granularity) => {
    const { currentPeriodIndex } = getPeriodInfo(newGranularity, campaignStart, campaignEnd, timezone, dataEndBoundMs);
    setState(prev => ({ ...prev, granularity: newGranularity, periodIndex: currentPeriodIndex }));
  };

  const series = buildSeries(
    windowedData,
    effectiveShowPredictions,
    seriesFilter,
    effectiveChartMode,
    {
      baseline: BASELINE_COLOR,
      uplift: UPLIFT_COLOR
    },
    timezone
  );

  const xAxisTickInterval = isHourly ? HOURLY_TICK_INTERVAL_MS : DAY_TICK_INTERVAL_MS;
  const axisTimestamps = [
    ...windowedData.baseline.map(p => new Date(p.date).getTime()),
    ...(effectiveShowPredictions ? windowedData.baselinePrediction.map(p => new Date(p.date).getTime()) : [])
  ];
  const totalTicks = axisTimestamps.length
    ? Math.round((Math.max(...axisTimestamps) - Math.min(...axisTimestamps)) / xAxisTickInterval) + 1
    : 1;
  const xAxisLabelStep = Math.max(1, Math.ceil(totalTicks / MAX_X_AXIS_LABELS));

  const chartOptions = {
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      height: CHART_HEIGHT_PX,
      type: effectiveChartMode === 'areas' ? 'areaspline' : 'column'
    },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      areaspline: { marker: { enabled: false } },
      column: { borderRadius: 4, borderWidth: 0, grouping: false, minPointLength: 3 },
      series: { states: { hover: { brightness: -0.1 } } }
    },
    series,
    title: { text: null },
    tooltip: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      formatter(this: any): string {
        const date = moment.tz(this.x, timezone).format('dddd D MMMM HH:mm[h]');
        const isToday = getDayStart(this.x, timezone) === getTodayTimestamp(timezone);
        const todayText = isToday
          ? ` <span style="color:var(--color-status-pending-500)">(${formatMessage({
              defaultMessage: 'Today',
              id: 'driveToStore.footfallReport.trafficAnalysis.today'
            })})</span>`
          : '';
        const baselinePoint = this.points.find((p: any) => p.series.name === 'Baseline Visits');
        const baselinePredPoint = this.points.find((p: any) => p.series.name === 'Baseline Prediction');
        const rows = this.points
          .map((p: any) => {
            let displayValue: string | number = p.y ?? '—';
            if (p.series.name === 'Attributed Uplift' && p.y != null && baselinePoint?.y != null) {
              displayValue = `+${p.y - baselinePoint.y}`;
            } else if (p.series.name === 'Uplift Prediction' && p.y != null && baselinePredPoint?.y != null) {
              displayValue = `+${p.y - baselinePredPoint.y}`;
            }
            return `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${displayValue}</b>`;
          })
          .join('<br/>');
        return `<span class="trafficAnalysis__tooltip"><span class="trafficAnalysis__tooltipDate">${date}${todayText}</span>${rows}</span>`;
      },
      shadow: false,
      shared: true,
      useHTML: true
    },
    xAxis: {
      labels: {
        align: 'right',
        formatter(this: any): string {
          const label = moment.tz(this.value, timezone).format(isHourly ? 'HH:mm' : 'ddd D');
          if (getDayStart(this.value, timezone) === getTodayTimestamp(timezone)) {
            return `<span style="color:var(--color-status-pending-500)">● </span><span style="font:var(--font-info-600-bold)">${label}</span>`;
          }
          return label;
        },
        rotation: -45,
        step: xAxisLabelStep,
        style: {
          color: 'var(--color-sonata-primary-800, #162237)',
          font: 'var(--font-info-600-regular)'
        },
        useHTML: true
      },
      lineWidth: 0,
      plotLines: [
        {
          color: TODAY_LINE_COLOR,
          label: {
            style: {
              color: TODAY_LABEL_COLOR,
              fontSize: '12px',
              fontWeight: '700'
            }
          },
          value: getTodayTimestamp(timezone),
          width: 2,
          zIndex: 5
        }
      ],
      tickInterval: xAxisTickInterval,
      tickLength: 0,
      type: 'datetime'
    },
    yAxis: {
      endOnTick: false,
      gridLineColor: 'var(--color-sonata-primary-300, #DDE9FD)',
      labels: {
        style: {
          color: 'var(--color-sonata-li-primary-500, #9FA5A9)',
          font: 'var(--font-info-600-regular)'
        }
      },
      min: 0,
      startOnTick: false,
      title: { text: null }
    }
  };

  const filterLabel: Record<SeriesFilter, string> = {
    all: formatMessage({ defaultMessage: 'View all', id: 'driveToStore.footfallReport.trafficAnalysis.viewAll' }),
    baseline: formatMessage({
      defaultMessage: 'Baseline only',
      id: 'driveToStore.footfallReport.trafficAnalysis.baselineOnly'
    }),
    uplift: formatMessage({
      defaultMessage: 'Uplift only',
      id: 'driveToStore.footfallReport.trafficAnalysis.upliftOnly'
    })
  };

  const granularityLabel: Record<Granularity, string> = {
    all: formatMessage({
      defaultMessage: 'View all',
      id: 'driveToStore.footfallReport.trafficAnalysis.granularity.all'
    }),
    day: formatMessage({
      defaultMessage: 'Day',
      id: 'driveToStore.footfallReport.trafficAnalysis.granularity.day'
    }),
    month: formatMessage({
      defaultMessage: 'Month',
      id: 'driveToStore.footfallReport.trafficAnalysis.granularity.month'
    }),
    week: formatMessage({
      defaultMessage: 'Week',
      id: 'driveToStore.footfallReport.trafficAnalysis.granularity.week'
    })
  };

  const periodNavigatorAriaLabels = {
    first: formatMessage({
      defaultMessage: 'First period',
      id: 'driveToStore.footfallReport.trafficAnalysis.periodNavigator.first'
    }),
    last: formatMessage({
      defaultMessage: 'Last period',
      id: 'driveToStore.footfallReport.trafficAnalysis.periodNavigator.last'
    }),
    next: formatMessage({
      defaultMessage: 'Next period',
      id: 'driveToStore.footfallReport.trafficAnalysis.periodNavigator.next'
    }),
    previous: formatMessage({
      defaultMessage: 'Previous period',
      id: 'driveToStore.footfallReport.trafficAnalysis.periodNavigator.previous'
    })
  };

  return (
    <div className="trafficAnalysis">
      <div className="trafficAnalysis__header">
        <div className="trafficAnalysis__headerLeft">
          <span className="trafficAnalysis__icon fa-regular fa-chart-line-up" />
          <h3 className="trafficAnalysis__title">
            {formatMessage({
              defaultMessage: 'Traffic Analysis',
              id: 'driveToStore.footfallReport.trafficAnalysis.title'
            })}
          </h3>
          <IconTooltip
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
            place="top"
          >
            {formatMessage({
              defaultMessage:
                'Daily breakdown of organic vs. campaign-driven traffic. Hover through the chart to see the specific lift achieved per day.',
              id: 'driveToStore.footfallReport.trafficAnalysis.analysisTooltip'
            })}
          </IconTooltip>
        </div>
        <div className="trafficAnalysis__headerRight">
          {showPeriodNavigator && (
            <div className="trafficAnalysis__periodBar">
              <div className="trafficAnalysis__granularityTags">
                {GRANULARITY_OPTIONS.map(g => (
                  <Tag
                    id={`trafficAnalysis-granularity-${g}`}
                    key={g}
                    onClick={() => handleGranularityChange(g)}
                    status={granularity === g ? 'enabled' : 'information'}
                  >
                    {granularityLabel[g]}
                  </Tag>
                ))}
              </div>
              <PeriodNavigator
                ariaLabels={periodNavigatorAriaLabels}
                disabled={granularity === 'all'}
                onChange={idx => handleChange('periodIndex', idx)}
                periodIndex={periodIndex}
                periodLabels={periodInfo.periodLabels}
                totalPeriods={periodInfo.totalPeriods}
              />
            </div>
          )}
          <Checkbox
            active={effectiveShowPredictions}
            disabled={!canShowPredictions}
            id={`trafficAnalysis-showPredictions`}
            labelText={formatMessage({
              defaultMessage: 'Show predictions',
              id: 'driveToStore.footfallReport.trafficAnalysis.showPredictions'
            })}
            onClickAction={() => handleChange('showPredictions', !showPredictions)}
          />
          <IconTooltip
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
            place="top"
          >
            {formatMessage(
              canShowPredictions
                ? {
                    defaultMessage:
                      'Predictive data is modeled using historical footfall patterns, seasonal trends, and panel-based surveys.',
                    id: 'driveToStore.footfallReport.trafficAnalysis.showPredictionsTooltip'
                  }
                : {
                    defaultMessage: 'Predictions are no longer available {days} days after the campaign has ended.',
                    id: 'driveToStore.footfallReport.trafficAnalysis.showPredictionsUnavailableTooltip'
                  },
              canShowPredictions ? undefined : { days: POST_CAMPAIGN_DAYS }
            )}
          </IconTooltip>
        </div>
      </div>
      {isFuturePeriod ? (
        <div className="trafficAnalysis__emptyState" style={{ height: CHART_HEIGHT_PX }}>
          <EmptyState
            description={formatMessage({
              defaultMessage:
                'This period hasn’t happened yet. Turn on "Show predictions" to see the forecasted traffic.',
              id: 'driveToStore.footfallReport.trafficAnalysis.futurePeriodEmptyStateDescription'
            })}
            show
            title={formatMessage({
              defaultMessage: 'No data for this period yet',
              id: 'driveToStore.footfallReport.trafficAnalysis.futurePeriodEmptyStateTitle'
            })}
          />
        </div>
      ) : (
        <>
          <div className="trafficAnalysis__legend">
            <div className="trafficAnalysis__legendItems">
              <span className="trafficAnalysis__legendDot trafficAnalysis__legendDot--baseline" />
              <span className="trafficAnalysis__legendLabel">
                {formatMessage({
                  defaultMessage: 'Baseline Visits (Organic traffic)',
                  id: 'driveToStore.footfallReport.trafficAnalysis.baselineVisits'
                })}
              </span>
              <span className="trafficAnalysis__legendDot trafficAnalysis__legendDot--uplift" />
              <span className="trafficAnalysis__legendLabel">
                {formatMessage({
                  defaultMessage: 'Attributed Uplift (Campaign impact)',
                  id: 'driveToStore.footfallReport.trafficAnalysis.attributedUplift'
                })}
              </span>
              {effectiveShowPredictions && (
                <>
                  <span className="trafficAnalysis__legendDash trafficAnalysis__legendDash--baseline" />
                  <span className="trafficAnalysis__legendLabel">
                    {formatMessage({
                      defaultMessage: 'Baseline Prediction',
                      id: 'driveToStore.footfallReport.trafficAnalysis.baselinePrediction'
                    })}
                  </span>
                  <span className="trafficAnalysis__legendDash trafficAnalysis__legendDash--uplift" />
                  <span className="trafficAnalysis__legendLabel">
                    {formatMessage({
                      defaultMessage: 'Uplift Prediction',
                      id: 'driveToStore.footfallReport.trafficAnalysis.upliftPrediction'
                    })}
                  </span>
                </>
              )}
            </div>
            <div className="trafficAnalysis__filters">
              {FILTER_OPTIONS.map(f => (
                <Tag
                  id={`trafficAnalysis-filter-${f}`}
                  key={f}
                  onClick={() => handleChange('seriesFilter', f)}
                  status={seriesFilter === f ? 'enabled' : 'information'}
                >
                  {filterLabel[f]}
                </Tag>
              ))}
              {hasAreasPermission && (
                <ViewToggle
                  onChange={value => handleChange('chartMode', value)}
                  options={[
                    {
                      icon: 'fa-solid fa-chart-area',
                      label: formatMessage({
                        defaultMessage: 'Areas',
                        id: 'driveToStore.footfallReport.trafficAnalysis.areas'
                      }),
                      value: 'areas' as ChartMode
                    },
                    {
                      icon: 'fa-solid fa-chart-simple',
                      label: formatMessage({
                        defaultMessage: 'Bars',
                        id: 'driveToStore.footfallReport.trafficAnalysis.bars'
                      }),
                      value: 'columns' as ChartMode
                    }
                  ]}
                  value={chartMode}
                  variant="interaction"
                />
              )}
            </div>
          </div>
          <div className="trafficAnalysis__chart">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
        </>
      )}
    </div>
  );
}
